import {ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {VRGroupService} from '../../vrgroup.service';
import {BaseComponent} from '../../../../components/general/base/base.component';
import { TrainDependency, ScheduleTrain, RunningTrain } from '../../common/train';
import {format} from 'date-fns/esm';
import {Subject} from 'rxjs';
import {parseToDate} from '../../../../shared/utils';
import {TranslateService} from '@ngx-translate/core';
import {_} from '../../../../shared/utils';
import { ServiceAnswer } from '../../../../shared/service-answer';
import { AlarmService } from '../../../../alarm/alarm.service';
import { AbstractDataModel } from '../../../../shared/abstract-data.model';
import { BackendAlarm } from '../../../../alarm/alarm-definitions/backend-alarm';
import { HttpParams } from '@angular/common/http';
import { LoadingConfig } from '../../../../components/qm-directives/loading.directive';
import { ChartEvent } from '../../../../components/google-charts/google-charts/events.model';
import { InfoActionService } from '../../../../element-utils/element-actions/info-action/info-action.service';
import { DependencyDisplayType, DependencyTrains, TrainInfo, WrappedDependency } from './types';
import {
  getCompositionStringFromLegacyDependencies, getDependantByTripNumber,
  getNodeTemplate, getStationTemplate, isValidDependeeTrain } from './utils';
import { TrainDependencyAlarmPayload } from '../../../../alarm/alarm-definitions/train-dependency.alarm';
import { MapService } from '~app/shared/map/map.service';

/**
 * TODO: rework this component to provide better support for inconsistent data from
 * both mylly and konegis.
 */
@Component({
  selector: 'qm-vrgroup-train-dependencies',
  templateUrl: './train-dependencies.component.html',
  styleUrls: ['./train-dependencies.component.scss']
})
export class TrainDependenciesComponent extends BaseComponent implements OnInit, OnDestroy, OnChanges {
  @Input()
  public train: RunningTrain;
  public columnNames: ['Name', 'Manager', 'Tooltip'];
  public options: {
    title: 'Organizational Chart View',
    allowHtml: true,
    allowCollapse: true,
    nodeClass: 'defaultNode',
    selectedNodeClass: 'selectedNode',
  };

  public myDeps = [];

  public dependencies: TrainDependency[][] = [];
  public loadingConfig: LoadingConfig = {
    messages: {
      empty: _('Equipment information is not available'),
    }
  };
  private scheduleMap: Map<string, ScheduleTrain> = new Map<string, ScheduleTrain>();
  /**
   * Subscription manager
   * @type {Subject<any>}
   */
  private ngUnsubscribe: Subject<void> = new Subject();

  /**
   * @param {VRGroupService} vrGroupService
   * @param {ChangeDetectorRef} changeDetector
   */
  constructor(private vrGroupService: VRGroupService,
              private translate: TranslateService,
              private changeDetector: ChangeDetectorRef,
              private alarmService: AlarmService,
              private mapService: MapService,
              private infoActionService: InfoActionService) {
    super();
    this.name = 'TrainDependenciesComponent';
  }

  ngOnInit() {
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.train.currentValue?._id !== changes.train.previousValue?._id) {
      this.loading = true;
      this.myDeps = [];
      this.scheduleMap.clear();
      if (this.train?.tripNumber && this.train?.tripDate) {
        this.addTrainDependencyNodes(this.train.tripNumber + '', this.train.tripDate);
      } else {
        console.warn('Train data missing');
        this.loading = {isOk: true, data: []};
      }
    }
  }

  /**
   * Angular destructor hook
   */
  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private addStation(parentId: string, stationShortCode: string) {
    this.myDeps.push([{
      v: parentId + '_' + stationShortCode,
      f: getStationTemplate(stationShortCode)
    }, parentId, '']);
  }

  private async addTrainDependencyNodes(
    tripNumber: string,
    tripDate: string,
  ) {
    try {
      const {dependeeTrains, selectedTrainAsDependency} = await this.getDependeeTrains(tripNumber, tripDate, 1);
      const lastDependeeId = await this.addDependeeTrainNodes(dependeeTrains);
      const selectedTrainId = await this.addSelectedTrainNode(this.train, lastDependeeId, selectedTrainAsDependency);
      this.addDependentTrainNodes(tripNumber, tripDate, selectedTrainId, 1);
    } catch (err) {
      console.warn(err);
    }
  }

  private async getDependeeTrains(tripNumber: string, tripDate: string, limit: number = 1) {
    const dependeeTrains: TrainDependency[] = [];
    let dependeeTrain: TrainDependency;
    let trainDependencies = await this.getTrainDependencies(tripNumber, tripDate);
    /**selectedTrainAsDependency is used for showing composition information for selected/current train after it has arrived */
    let selectedTrainAsDependency: TrainDependency;
    if (trainDependencies?.dependeeTrains?.length) {

      /**Get running train as dependency */
      const dependeeDependencies = await this.getTrainDependencies(
        trainDependencies.dependeeTrains[0].trainNumber,
        trainDependencies.dependeeTrains[0].operatingDate);

      selectedTrainAsDependency = getDependantByTripNumber(dependeeDependencies, tripNumber);

      if (selectedTrainAsDependency) {
        dependeeTrain = trainDependencies.dependeeTrains.find(isValidDependeeTrain);
        if (dependeeTrain) {
          dependeeTrains.push(dependeeTrain);
        } else {
          return {dependeeTrains, selectedTrainAsDependency};
        }
        for (let i = 1; i < limit; i++) {
          /**Currently we are showing only one dependee train per dependant and thats why we pick the first dependee... */
          trainDependencies = await this.getTrainDependencies(dependeeTrains[0].trainNumber, dependeeTrains[0].operatingDate);
          selectedTrainAsDependency = i === 1 ? getDependantByTripNumber(trainDependencies, tripNumber) : selectedTrainAsDependency;
          /**Pick only train dependencies with compositions or locs and which location is different than ILR or NOK*/
          dependeeTrain = trainDependencies.dependeeTrains.find(isValidDependeeTrain);
          if (dependeeTrain) {
            dependeeTrains.unshift(dependeeTrain);
          } else {
            return {dependeeTrains, selectedTrainAsDependency};
          }
        }
      } else {
        /**no data available */
        this.loading = {isOk: true, data: []};
        return Promise.reject('No data available');
      }
    } else {
      this.loading = {isOk: true, data: []};
      return Promise.reject('No dependee trains available');
    }
    return {dependeeTrains, selectedTrainAsDependency};
  }

  private async addDependeeTrainNodes(dependeeTrains: TrainDependency[]) {
    let lastDependeeId = '';
    for (const dependee of dependeeTrains) {
      const scheduleTrain = await this.getTrainSchedule(dependee.operatingDate + '_' + dependee.trainNumber);
      const train = await this.trainDependencyToTrainInfo(dependee, scheduleTrain, null, true);
      this.addStation(lastDependeeId, train.depStation);
      this.addTrainEquipmentNode(train, lastDependeeId + '_' + train.depStation, 'dependee');
      lastDependeeId = dependee.operatingDate + '_' + dependee.trainNumber;
    }
    return lastDependeeId;
  }

  /**
   * query train dependencies from mylly
   * @param tripNumber
   * @param tripDate
   */
  private async getTrainDependencies(tripNumber: string, tripDate: string): Promise<DependencyTrains> {
    return new Promise((resolve, reject) => {
      this.vrGroupService.getTrainDependencies(tripNumber, tripDate)
        .then(serviceAnswer => {
          if (serviceAnswer.isOk) {
            resolve(serviceAnswer.data);
          } else {
            this.loading = serviceAnswer;
            reject(serviceAnswer);
          }
        });
    });
  }

  private async addSelectedTrainNode(
    selectedTrain: RunningTrain,
    parentId: string,
    selectedTrainAsDependency: TrainDependency
  ): Promise<string> {
    let train: TrainInfo;
    const selectedTrainDependees = (await this.getTrainDependencies(selectedTrain.tripNumber + '', selectedTrain.tripDate)).dependeeTrains;
    const compositionString = this.getCompositionString(selectedTrainDependees);
    if (selectedTrain.state === 'RUNNING') {
      this.addStation(parentId, selectedTrain.depStation);
      train = await this.runningTrainToTrainInfo(selectedTrain, compositionString);
      this.addTrainEquipmentNode(train, parentId + '_' + selectedTrain.depStation, 'running');
    } else {
      const scheduleTrain = await this.getTrainSchedule(selectedTrain.tripDate + '_' + selectedTrain.tripNumber);
      train = await this.trainDependencyToTrainInfo(selectedTrainAsDependency, scheduleTrain, compositionString, false);
      this.addStation(parentId, train.depStation);
      this.addTrainEquipmentNode(train, parentId + '_' + train.depStation, 'default');
    }
    return train.id;
  }

  private async runningTrainToTrainInfo(runningTrain: RunningTrain, compositionString: string): Promise<TrainInfo> {
    const scheduleTrain = await this.getTrainSchedule(runningTrain.tripDate + '_' + runningTrain.tripNumber);
    const depTimetableEntry = scheduleTrain?.timetable.length ? scheduleTrain.timetable[0] : undefined;
    return {
      id: runningTrain.tripDate + '_' + runningTrain.tripNumber,
      title: runningTrain.category + ' ' + runningTrain.tripNumber,
      arrTime: format(parseToDate(runningTrain.arrTime), 'dd.MM HH:mm'),
      depTime: format(parseToDate(runningTrain.depTime), 'dd.MM HH:mm'),
      depTimeActual: depTimetableEntry?.actual ? format(parseToDate(depTimetableEntry.actual), 'HH:mm') : undefined,
      location: '??',
      composition: compositionString,
      depStation: runningTrain.depStation,
      arrStation: runningTrain.arrStation,
      calculatedDelta: runningTrain.calculatedDelta,
    };
  }

  private async trainDependencyToTrainInfo(
    dep: TrainDependency,
    scheduleTrain: ScheduleTrain,
    composition: string | null,
    dependee = false
  ): Promise<TrainInfo> {
    if (!dep) {
      this.loading = {isOk: true, data: []};
      return Promise.reject('No train dependency available');
    }
    const noInfoAvailable = this.translate.instant(_('Information is not available'));
    const train: TrainInfo = {
      id: dep.operatingDate + '_' + dep.trainNumber,
      title: '',
      arrTime: noInfoAvailable,
      depTime: noInfoAvailable,
      composition: composition ? composition : dep.subDependencies ? this.getCompositionString([dep]) : '',
      depStation: '',
      arrStation: '',
      location: dep.location
    };
    const arrTimetableEntry = scheduleTrain?.timetable?.length ? scheduleTrain.timetable[scheduleTrain.timetable.length - 1] : undefined;
    const depTimetableEntry = scheduleTrain?.timetable.length ? scheduleTrain.timetable[0] : undefined;

    if (arrTimetableEntry) {
      train.arrTime = format(parseToDate(arrTimetableEntry.schedule as any), 'dd.MM HH:mm');
      train.arrTimeActual = arrTimetableEntry.actual ? format(parseToDate(arrTimetableEntry.actual), 'HH:mm') : undefined;
    } else if (!dependee) {
      train.arrTime = format(parseToDate(dep.depBeginDateTime as any), 'dd.MM HH:mm');
    }

    try {
      train.depStation = scheduleTrain ? scheduleTrain.depStation : '??';
      train.depTime = scheduleTrain?.depTime
        ? format(parseToDate(scheduleTrain.depTime), 'dd.MM HH:mm')
        : noInfoAvailable;
      train.depTimeActual = depTimetableEntry?.actual ? format(parseToDate(depTimetableEntry.actual), 'HH:mm') : undefined;
      train.arrStation = scheduleTrain?.arrStation ? scheduleTrain.arrStation : '??';
      train.title = scheduleTrain?.category ? scheduleTrain.category + ' ' + dep.trainNumber : dep.trainNumber;
      ///// this query do have extra cost, travels all the way to mylly api.
      if (train.arrStation === '??' && train.depStation === '??') {
        console.warn('no schedule found for train' + train.title + ', use mylly instead');
        const deps2 = await this.getTrainDependencies(train.title, dep.operatingDate);
        if (deps2) {
          /**arrStation is used only for dependant trains */
          train.arrStation = deps2.dependentTrains?.length ? deps2.dependentTrains[0].location : '??';
          /**depStation is used only for dependee trains */
          train.depStation = deps2.dependeeTrains?.length ? deps2.dependeeTrains[0].location : '??';
        } else {
          console.error('getTrainDependencies() failed');
        }
      }

    } catch (e) {
      this.loading = {isOk: true, data: []};
      return Promise.reject(e);
    }
    return train;
  }

  private async addDependentTrainNodes(tripNumber: string, tripDate: string, parentId: string, recursionLevel: number) {
    if (this.myDeps.length > 25) {
      this.loading = false;
      this.changeDetector.markForCheck();
      console.log('All trains done!');
      return;
    }

    try {
      console.log('Walking ' + tripNumber + ' :: ' + tripDate);
      const deps = await this.getTrainDependencies(tripNumber, tripDate);
      const depArr = deps.dependentTrains;

      if (!(depArr && depArr.length > 0)) {
        if (recursionLevel > 1) {
          this.loading = false;
        } else {
          this.loading = {isOk: true, data: []};
        }
        this.changeDetector.markForCheck();
        return;
      }

      // API sometimes return dependencies that are "empty"
      const depArrFixed = depArr.filter(dep => {
        if (!dep.dependentLocs && !dep.dependent_compositions && !dep.subDependencies) {
          // do not add
          return false;
        } else {
          return dep;
        }
      });

      let wrappedDependencies: WrappedDependency[] = [];
      if (recursionLevel === 1) {
        wrappedDependencies = await this.createWrappedDependenciesWithConflictInfo(depArrFixed, tripNumber, tripDate);
      } else {
        wrappedDependencies = depArrFixed.map(dep => {
          const wrappedDep: WrappedDependency = {
            dep,
            'displayType': 'default'
          };
          return wrappedDep;
        });
      }

      for (const wrappedDep of wrappedDependencies) {
        const dep = wrappedDep.dep;
        const scheduleTrain = await this.getTrainSchedule(dep.operatingDate + '_' + dep.trainNumber);
        const train = await this.trainDependencyToTrainInfo(dep, scheduleTrain, null);

        this.addStation(parentId, dep.location);
        this.addTrainEquipmentNode(train, parentId + '_' + dep.location, wrappedDep.displayType);

        if (train.arrStation !== 'ILR' && train.arrStation !== '') {
          this.addDependentTrainNodes(dep.trainNumber, dep.operatingDate, dep.operatingDate + '_' + dep.trainNumber, recursionLevel + 1);
          return true;
        }
        this.loading = false;
        this.changeDetector.markForCheck();
        return false;
      }
      //// TODO, this is temporary and additional checking if there is no data available from endpoint to display!
      //// Should we show something, or nothing, this is just to show some info for user!
      if (!wrappedDependencies || wrappedDependencies.length === 0) {
        this.myDeps.push([{
          v: 'NO-DATA',
          f: 'mylly-no-equipment-data'
        }, parentId, '']);
        this.loading = false;
        this.changeDetector.markForCheck();
        return false;
      }
    } catch (e) {
      console.warn(e);
      this.loading = {isOk: true, data: []};
      this.changeDetector.markForCheck();
      return Promise.reject('Something went wrong when adding dependent trains');
    }
  }

  private getCompositionString(deps: TrainDependency[]) {
    const composition = {locomotives: '', wagons: ''};
    if (deps.length) {
      deps.forEach(dep => {
        if (dep.subDependencies) {
          const comp = this.vrGroupService.compositionStringFromTrainSubdependency(dep.subDependencies);
          composition.locomotives = composition.locomotives.concat(comp.locomotives) + ' ';
          composition.wagons = composition.wagons.concat(comp.wagons);
        } else {
          const [locs, depCompositions] = getCompositionStringFromLegacyDependencies(dep);
          composition.locomotives = composition.locomotives.concat(locs);
          composition.wagons = composition.wagons.concat(depCompositions);
        }
      });
    }
    // div is there to prevent text from wrapping
    return `<div>${composition.locomotives} ${composition.wagons}</div>`;
  }

  private async createWrappedDependenciesWithConflictInfo(depArrFixed: TrainDependency[], tripNumber: any, trainDate: string) {
    const wrappedDeps: WrappedDependency[] = [];
    for (const dep of depArrFixed) {
      const conflictAlarmReqFilters = {
        active: 'true',
        alarmId: `"${tripNumber}"`, // is this correct ?
        parentId: `"${dep.trainNumber}"`,
        type: 'vrTrainConflict'
      };
      const query = new HttpParams({
        fromObject: conflictAlarmReqFilters
      }).toString();
      let serviceAnswer: ServiceAnswer<AbstractDataModel<BackendAlarm>>;
      try {
        serviceAnswer = await this.alarmService.queryAlarms(query);
      } catch (e) {
        console.error(e);
      }
      let isConflicted = false;
      if (serviceAnswer.isOk && serviceAnswer.data && serviceAnswer.data.total > 0) {
        const alarms = serviceAnswer.data.data;
        isConflicted = alarms &&
                       alarms.length > 0 &&
                       alarms
                        .some(a => (a.payload as TrainDependencyAlarmPayload).trainDate === trainDate );
      }

      wrappedDeps.push({
        'dep': dep,
        displayType: isConflicted ? 'conflicted' : 'default'
      });
    }
    return wrappedDeps;
  }

  private async getTrainSchedule(trainId: string): Promise<ScheduleTrain> {
    if (this.scheduleMap.has(trainId)) {
      return this.scheduleMap.get(trainId);
    } else {
      const scheduleTrain = await this.vrGroupService.getTrainSchedule(trainId);
      if (scheduleTrain.isOk) {
        this.scheduleMap.set(trainId, scheduleTrain.data.data[0]);
        return scheduleTrain.data.data[0];
      }
      return undefined;
    }
  }

  private addTrainEquipmentNode(
    train: any, parentId: string, displayType: DependencyDisplayType
  ) {
    this.myDeps.push([{
      v: train.id,
      f: this.getTrainEquipmentNodeTemplate(train, displayType)
    }, parentId, '']);
  }

  private getTrainEquipmentNodeTemplate(train: TrainInfo, displayType: DependencyDisplayType) {
    let nodeClassPostfix: string;
    switch (displayType) {
      case 'conflicted':
        nodeClassPostfix = '-danger';
        break;
      case 'running':
        nodeClassPostfix = '-success';
        break;
      case 'dependee':
        nodeClassPostfix = '-dependee';
        break;
      case 'default':
        nodeClassPostfix = '';
    }
    return getNodeTemplate(train, nodeClassPostfix, this.translate.instant(_('Departing')), this.translate.instant(_('Arriving')));
  }

  public handleChartSelect(selectionEvent: ChartEvent[]) {
    if (selectionEvent && selectionEvent.length) {
      const depIndex = selectionEvent[0].row;
      const dep = depIndex < this.myDeps.length ? this.myDeps[selectionEvent[0].row] : null;
      if (dep) {
        const splittedId = dep[0].v.split('_');
        if (splittedId.length === 2) {
          this.vrGroupService.getTrainMapElement(splittedId[1], splittedId[0])
          .then(feature => {
            this.mapService.changeCurrentFeature(feature);
            this.infoActionService.openDetails(feature.layer, feature, 'dependencies');
          })
          .catch(err => {
            console.warn('getTrainMapElement failed: ', err);
          });
        }
      }
    }
  }
}
